<pt-breadcrumbs class="bg-white" [breadcrumbs]="breadcrumbs$ | async" />
<div class="border-b border-solid border-slate-300"></div>

<div class="layout-padding mx-auto h-full max-h-[90vh] max-w-screen-2xl">
  @if (store.loading$ | async) {
    <mat-progress-bar mode="indeterminate" />
  }

  @if (store.referral$ | async; as referral) {
    <pr-content-container height="h-full" overflow="overflow-hidden">
      <div class="flex h-full flex-col gap-4">
        <pr-referral-toolbar
          [referral]="store.referral$ | async"
          [isSaving]="referralService.isSaving()"
        >
          <pr-referral-status
            ngProjectAs="referral-status"
            [status]="referral.status"
          />
          @if (referralMediaService.uploadInProgress()) {
            <div
              ngProjectAs="uploading-content"
              class="text-primary-500 my-2 flex items-center justify-center gap-2"
            >
              <mat-spinner diameter="24" />
              <span>Uploading...</span>
            </div>
          }
          <div class="flex items-center">
            <div class="relative">
              @if (referralEmailService.isSending()) {
                <div class="absolute inset-0 flex items-center justify-center">
                  <mat-progress-spinner diameter="48" mode="indeterminate" />
                </div>
              }
              <button
                mat-icon-button
                color="primary"
                [matTooltip]="
                  referral.emailInvitationSent
                    ? 'Notify Recipient of Changes'
                    : 'Send Referral'
                "
                [disabled]="referralEmailService.isSending()"
                (click)="sendReferral()"
              >
                <mat-icon>send</mat-icon>
              </button>
            </div>
            <pr-referral-followers
              [referral]="referral"
              [currentUser]="store.currentUser$ | async"
            />
            <pr-referral-menu-button
              [referral]="referral"
              (updateStatus)="store.setChanges({ status: $event })"
              (deleteReferral)="deleteReferral($event)"
              (preview)="preview()"
            />
          </div>
        </pr-referral-toolbar>

        <mat-tab-group
          class="flex-1"
          mat-align-tabs="start"
          mat-stretch-tabs="false"
          fitInkBarToContent
        >
          <mat-tab>
            <ng-template mat-tab-label>
              <span class="flex items-center gap-2 text-sm text-black">
                <mat-icon class="icon-sm">chat</mat-icon> Messages
              </span>
            </ng-template>
            <pr-referral-image-upload
              [referral]="store.referral$ | async"
              [currentUser]="store.currentUser$ | async"
            >
              <pr-referral-communication
                [referral]="store.referral$ | async"
                [messages]="store.messages$ | async"
                [menuItems]="menuItems()"
                [interactions]="store.interactions$ | async"
                [media]="store.media$ | async"
                [clinicalNotes]="store.clinicalNotes$ | async"
                [notes]="store.notes$ | async"
                [currentUser]="store.currentUser$ | async"
                [canManagePatientMedia]="true"
                [canAddNotes]="true"
                [showInteractions]="true"
              >
                <pr-referral-attachment-menu
                  ngProjectAs="attach-file"
                  [referral]="store.referral$ | async"
                  [patient]="store.patient$ | async"
                  [media]="store.media$ | async"
                  [currentUser]="store.currentUser$ | async"
                  [clinicalNotes]="store.clinicalNotes$ | async"
                  (clinicalNotesChanged)="handleClinicalNotesChanged($event)"
                />
              </pr-referral-communication>
            </pr-referral-image-upload>
          </mat-tab>

          <mat-tab>
            <ng-template mat-tab-label>
              <span class="flex items-center gap-2 text-sm text-black">
                <mat-icon class="icon-sm">attach_file</mat-icon> Files
              </span>
            </ng-template>
            <pr-referral-image-upload
              [referral]="store.referral$ | async"
              [currentUser]="store.currentUser$ | async"
            >
              <pr-referral-media
                [referral]="store.referral$ | async"
                [media]="store.media$ | async"
                [currentUser]="store.currentUser$ | async"
                [canManagePatientMedia]="true"
              >
                <pr-referral-attachment-menu
                  ngProjectAs="attach-file"
                  [hideClinicalNotes]="true"
                  [referral]="store.referral$ | async"
                  [patient]="store.patient$ | async"
                  [media]="store.media$ | async"
                  [currentUser]="store.currentUser$ | async"
                  [clinicalNotes]="store.clinicalNotes$ | async"
                  (clinicalNotesChanged)="handleClinicalNotesChanged($event)"
                />
                <pr-referral-attachment-menu
                  ngProjectAs="empty-state"
                  layout="expanded"
                  [hideClinicalNotes]="true"
                  [referral]="store.referral$ | async"
                  [patient]="store.patient$ | async"
                  [media]="store.media$ | async"
                  [currentUser]="store.currentUser$ | async"
                  [clinicalNotes]="store.clinicalNotes$ | async"
                  (clinicalNotesChanged)="handleClinicalNotesChanged($event)"
                />
              </pr-referral-media>
            </pr-referral-image-upload>
          </mat-tab>

          <mat-tab>
            <ng-template mat-tab-label>
              <span class="flex items-center gap-2 text-sm text-black">
                <mat-icon class="icon-sm">info</mat-icon> Information
              </span>
            </ng-template>

            <div class="flex h-full flex-col gap-4 overflow-auto p-4">
              <pr-referral-patient-details
                [editable]="true"
                [patient]="store.patient$ | async"
                [referral]="store.referral$ | async"
                [primaryContact]="store.primaryContact$ | async"
                [contactNumbers]="store.contactNumbers$ | async"
                (openPatientInNewTab)="
                  referralService.navigateToPatient($event, true)
                "
                (sharedPatientDetailsChanges)="
                  store.updateOptionalPatientDetails($event)
                "
              />

              <pr-referral-participant-details
                [participant]="store.referrer$ | async"
                [practice]="store.practice$ | async"
                [title]="'Referrer Details'"
                [editable]="true"
              />

              @if (store.recipient$ | async; as recipient) {
                <pr-referral-participant-details
                  [participant]="recipient"
                  [practice]="store.practice$ | async"
                  [title]="'Recipient Details'"
                  [editable]="true"
                />
              }
            </div>
          </mat-tab>

          <mat-tab>
            <ng-template mat-tab-label>
              <span class="flex items-center gap-2 text-sm text-black">
                <mat-icon class="icon-sm">person</mat-icon> Patient Letter
              </span>
            </ng-template>
            @if (store.patient$ | async; as patient) {
              @if (store.practice$ | async; as practice) {
                @if (store.currentUser$ | async; as currentUser) {
                  <pr-referral-patient-letter
                    [referral]="referral"
                    [patient]="patient"
                    [practice]="practice"
                    [currentUser]="currentUser"
                    [media]="(store.media$ | async) ?? []"
                    [clinicalNotes]="(store.clinicalNotes$ | async) ?? []"
                    [patientClinicalNotes]="
                      (store.patientClinicalNotes$ | async) ?? []
                    "
                  />
                }
              }
            }
          </mat-tab>
        </mat-tab-group>
      </div>
    </pr-content-container>
  }
</div>
